import {
  BlockStack,
  Button,
  Checkbox,
  Divider,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import type {
  WheelConfigV1,
  WheelLeadField,
  WheelRewardType,
  WheelSegmentV1,
} from "../../types/wheel";
import {
  WHEEL_REWARD_TYPES,
  isWheelRewardType,
  withWheelEngineDefaults,
} from "../../types/wheel";
import { buildDefaultWheelConfigForTemplate, type WheelCatalogTemplateId } from "./wheelCatalogTemplates";

function rewardTypeLabel(t: WheelRewardType): string {
  switch (t) {
    case "message":
      return "Message";
    case "discount_percent":
      return "Discount %";
    case "discount_code":
      return "Discount code";
    case "points":
      return "Loyalty points";
    case "free_shipping":
      return "Free shipping";
  }
}

function defaultRewardValueFor(type: WheelRewardType): string | number {
  switch (type) {
    case "message":
    case "free_shipping":
      return "";
    case "discount_code":
      return "SAVE10";
    case "discount_percent":
      return 10;
    case "points":
      return 0;
  }
}

function coerceRewardType(raw: unknown): WheelRewardType {
  if (raw === "discount") return "discount_percent";
  if (isWheelRewardType(raw)) return raw;
  return "message";
}

function coerceRewardValue(raw: unknown, rewardType: WheelRewardType): string | number {
  switch (rewardType) {
    case "message":
    case "free_shipping":
    case "discount_code":
      return raw == null ? "" : String(raw);
    case "discount_percent": {
      if (typeof raw === "number" && Number.isFinite(raw)) {
        return Math.min(100, Math.max(0, raw));
      }
      const n = parseFloat(String(raw));
      return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 10;
    }
    case "points": {
      if (typeof raw === "number" && Number.isFinite(raw)) {
        return Math.max(0, Math.round(raw));
      }
      const n = parseInt(String(raw), 10);
      return Number.isFinite(n) ? Math.max(0, n) : 0;
    }
  }
}

type LeadChoice = "email" | "phone" | "both";

function leadToChoice(fields: WheelLeadField[] | undefined): LeadChoice {
  const f = fields?.length ? fields : (["email"] as WheelLeadField[]);
  const hasE = f.includes("email");
  const hasP = f.includes("phone");
  if (hasE && hasP) return "both";
  if (hasP) return "phone";
  return "email";
}

function choiceToLeadFields(choice: LeadChoice): WheelLeadField[] {
  if (choice === "both") return ["email", "phone"];
  if (choice === "phone") return ["phone"];
  return ["email"];
}

const rewardOptions = WHEEL_REWARD_TYPES.map((v) => ({
  label: rewardTypeLabel(v),
  value: v,
}));

export interface WheelTemplateConfigFormProps {
  templateId: WheelCatalogTemplateId;
  value: WheelConfigV1;
  onChange: (next: WheelConfigV1) => void;
}

/**
 * Template defaults editor: segments + rewards, and lead fields when template is lead capture.
 * Interaction mode is fixed by template (not switchable here).
 */
export function WheelTemplateConfigForm({
  templateId,
  value,
  onChange,
}: WheelTemplateConfigFormProps) {
  const updateSegment = (index: number, patch: Partial<WheelSegmentV1>) => {
    const segments = value.segments.map((s, i) => (i === index ? { ...s, ...patch } : s));
    onChange(withWheelEngineDefaults({ ...value, segments }));
  };

  const addSegment = () => {
    onChange(
      withWheelEngineDefaults({
        ...value,
        segments: [
          ...value.segments,
          {
            id: crypto.randomUUID(),
            label: "New prize",
            weight: 1,
            rewardType: "message",
            rewardValue: "Thanks for playing",
            enabled: true,
          },
        ],
      })
    );
  };

  const removeSegment = (index: number) => {
    if (value.segments.length <= 2) return;
    onChange(
      withWheelEngineDefaults({
        ...value,
        segments: value.segments.filter((_, i) => i !== index),
      })
    );
  };

  const leadChoice =
    templateId === "lead_capture" && value.interaction?.type === "lead_capture"
      ? leadToChoice(value.interaction.requiredFields)
      : "email";

  const setLeadChoice = (choice: LeadChoice) => {
    if (templateId !== "lead_capture") return;
    onChange(
      withWheelEngineDefaults({
        ...value,
        interaction: {
          type: "lead_capture",
          requiredFields: choiceToLeadFields(choice),
        },
      })
    );
  };

  return (
    <BlockStack gap="400">
      <Text as="p" variant="bodyMd" tone="subdued">
        {templateId === "lead_capture"
          ? "Lead Capture — shoppers submit contact details before spinning."
          : "Instant Discount — shoppers spin immediately; prioritize discount codes and % off outcomes."}
      </Text>

      {templateId === "lead_capture" && (
        <Select
          label="Required contact fields"
          options={[
            { label: "Email only", value: "email" },
            { label: "Phone only", value: "phone" },
            { label: "Email and phone", value: "both" },
          ]}
          value={leadChoice}
          onChange={(v) => setLeadChoice(v as LeadChoice)}
        />
      )}

      <Divider />

      <Text variant="headingSm" as="h3">
        Segments & rewards
      </Text>
      <Text as="p" variant="bodySm" tone="subdued">
        These defaults are applied when you add a campaign variant from this template (stored in this
        browser until you save).
      </Text>

      {value.segments.map((seg, index) => (
        <div
          key={seg.id}
          style={{
            border: "1px solid #e3e3e3",
            borderRadius: 8,
            padding: "0.75rem",
          }}
        >
          <BlockStack gap="200">
            <TextField
              label="Label"
              value={seg.label}
              onChange={(v) => updateSegment(index, { label: v })}
              autoComplete="off"
            />
            <TextField
              label="Weight"
              type="number"
              value={String(seg.weight)}
              onChange={(v) =>
                updateSegment(index, { weight: Math.max(0.0001, parseFloat(v) || 0) })
              }
              autoComplete="off"
              min={0.0001}
              step={0.1}
            />
            <Select
              label="Reward type"
              options={rewardOptions}
              value={seg.rewardType}
              onChange={(v) => {
                const next = v as WheelRewardType;
                updateSegment(index, {
                  rewardType: next,
                  rewardValue: defaultRewardValueFor(next),
                });
              }}
            />
            {seg.rewardType === "discount_code" ? (
              <TextField
                label="Checkout code"
                helpText="Shown to the customer after a winning spin."
                value={String(seg.rewardValue)}
                onChange={(v) => updateSegment(index, { rewardValue: v })}
                autoComplete="off"
              />
            ) : seg.rewardType === "message" || seg.rewardType === "free_shipping" ? (
              <TextField
                label={seg.rewardType === "message" ? "Message text" : "Note (optional)"}
                value={String(seg.rewardValue)}
                onChange={(v) => updateSegment(index, { rewardValue: v })}
                autoComplete="off"
                multiline={seg.rewardType === "message" ? 2 : 1}
              />
            ) : (
              <TextField
                label={seg.rewardType === "discount_percent" ? "Percent (0–100)" : "Points (integer)"}
                type="number"
                value={String(seg.rewardValue)}
                onChange={(v) => {
                  const n = parseFloat(v);
                  if (seg.rewardType === "discount_percent") {
                    updateSegment(index, {
                      rewardValue: Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0,
                    });
                  } else {
                    updateSegment(index, {
                      rewardValue: Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0,
                    });
                  }
                }}
                autoComplete="off"
                min={0}
                max={seg.rewardType === "discount_percent" ? 100 : undefined}
              />
            )}
            <Checkbox
              label="Enabled"
              checked={seg.enabled}
              onChange={(v) => updateSegment(index, { enabled: v })}
            />
            <Button
              size="slim"
              tone="critical"
              onClick={() => removeSegment(index)}
              disabled={value.segments.length <= 2}
            >
              Remove segment
            </Button>
          </BlockStack>
        </div>
      ))}

      <Button onClick={addSegment}>Add segment</Button>
    </BlockStack>
  );
}

/** Normalize unknown persisted JSON into a usable wheel config for the template editor. */
export function normalizeTemplateWheelConfig(
  raw: unknown,
  templateId: WheelCatalogTemplateId
): WheelConfigV1 {
  const w = raw as Partial<WheelConfigV1> | null | undefined;
  if (!w?.segments || !Array.isArray(w.segments) || w.segments.length < 2) {
    return buildDefaultWheelConfigForTemplate(templateId);
  }
  return withWheelEngineDefaults({
    segments: w.segments.map((s) => {
      const rt = coerceRewardType(s.rewardType);
      return {
        id: String(s.id ?? crypto.randomUUID()),
        label: String(s.label ?? ""),
        weight: typeof s.weight === "number" && s.weight > 0 ? s.weight : 1,
        rewardType: rt,
        rewardValue: coerceRewardValue(s.rewardValue, rt),
        enabled: s.enabled !== false,
      };
    }),
    engineVersion: typeof w.engineVersion === "string" ? w.engineVersion : undefined,
    visualVariant: typeof w.visualVariant === "string" ? w.visualVariant : undefined,
    interaction:
      templateId === "lead_capture"
        ? {
            type: "lead_capture" as const,
            requiredFields:
              w.interaction?.type === "lead_capture" &&
              Array.isArray(w.interaction.requiredFields) &&
              w.interaction.requiredFields.length > 0
                ? w.interaction.requiredFields.filter(
                    (x): x is WheelLeadField => x === "email" || x === "phone"
                  )
                : ["email"],
          }
        : { type: "instant_discount" as const },
  });
}

